import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Check, CheckCheck, X, MessageSquare, UserCheck, UserX, FileText, AtSign, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../api';

interface Notification {
  id: string;
  type: string;
  title: string;
  content: string;
  candidate_id?: string;
  candidate_name?: string;
  need_id?: string;
  link?: string;
  is_read: number;
  created_at: string;
}

interface Props {
  onNavigate: (link: string) => void;
}

const typeIcons: Record<string, React.ReactNode> = {
  evaluation: <FileText size={14} className="text-primary-500" />,
  mention: <AtSign size={14} className="text-primary-500" />,
  screening: <UserCheck size={14} className="text-primary-500" />,
  interview_feedback: <MessageSquare size={14} className="text-gray-500" />,
  need_mention: <AtSign size={14} className="text-primary-500" />,
  need_comment: <MessageSquare size={14} className="text-primary-500" />,
};

const typeBgColors: Record<string, string> = {
  evaluation: 'bg-primary-50',
  mention: 'bg-primary-50',
  screening: 'bg-primary-50',
  interview_feedback: 'bg-gray-50',
  need_mention: 'bg-primary-50',
  need_comment: 'bg-primary-50',
};

function timeAgo(dateStr: string): string {
  // Server stores Beijing time via datetime('now','localtime') with TZ=Asia/Shanghai
  // For strings without timezone info, treat as Beijing time (+08:00)
  const now = new Date();
  let isoStr = dateStr.replace(' ', 'T');
  if (!/[Z+\-]\d{2}/.test(isoStr)) isoStr += '+08:00';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return dateStr;
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return '刚刚';
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return dateStr.split(' ')[0] || dateStr.split('T')[0];
}

export default function NotificationCenter({ onNavigate }: Props) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getNotifications({ limit: 50 });
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll for unread count every 30s
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const data = await getNotifications({ limit: 1 });
        setUnreadCount(data.unread_count || 0);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch full list when panel opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleMarkRead = async (id: string) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch {}
  };

  const handleClickNotification = async (n: Notification) => {
    if (!n.is_read) await handleMarkRead(n.id);
    if (n.link) {
      onNavigate(n.link);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        className={clsx(
          'relative p-2 rounded-lg transition-colors',
          open ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
        )}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-gray-500 rounded-full leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] max-h-[70vh] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col z-50 animate-modal-in">
          {/* Header */}
          <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-900">消息中心</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-[11px] text-primary-500 hover:text-primary-700 font-medium flex items-center gap-1"
                >
                  <CheckCheck size={13} />
                  全部已读
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-gray-100 text-gray-400">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 size={20} className="animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Bell size={28} className="mb-2 text-gray-300" />
                <p className="text-sm">暂无消息</p>
              </div>
            ) : (
              notifications.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleClickNotification(n)}
                  className={clsx(
                    'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-b border-gray-50 hover:bg-gray-50',
                    !n.is_read && 'bg-primary-50/40'
                  )}
                >
                  {/* Icon */}
                  <div className={clsx(
                    'shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5',
                    typeBgColors[n.type] || 'bg-gray-100'
                  )}>
                    {typeIcons[n.type] || <Bell size={14} className="text-gray-400" />}
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className={clsx(
                      'text-[13px] leading-snug',
                      !n.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'
                    )}>
                      {n.title}
                    </p>
                    {n.content && (
                      <p className="text-[11px] text-gray-400 mt-0.5 truncate">{n.content}</p>
                    )}
                    <p className="text-[10px] text-gray-300 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  {/* Unread dot */}
                  {!n.is_read && (
                    <div className="shrink-0 mt-2">
                      <div className="w-2 h-2 rounded-full bg-primary-500" />
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
